import React, { useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { useAnalyticsContext } from '../analytics-context';

const metricConfig = {
  pageViews: {
    key: 'pageViews',
    label: 'Page Views',
    color: '#2563eb',
  },
  sessions: {
    key: 'sessions',
    label: 'Sessions',
    color: '#10b981',
  },
  newUsers: {
    key: 'newUsers',
    label: 'New Users',
    color: '#f97316',
  },
  totalUsers: {
    key: 'totalUsers',
    label: 'Total Users',
    color: '#9333ea',
  },
} as const;

type MetricKey = keyof typeof metricConfig;

const GaTrend: React.FC = () => {
  const { ga, isCustomRangePending } = useAnalyticsContext();
  const trendError = ga.trend.error as any;
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('pageViews');
  const metric = metricConfig[selectedMetric];

  const trendSummary = useMemo(() => {
    const data = ga.trend.data;
    if (!data.length) {
      return { total: 0, change: 0, changePct: 0 };
    }
    const total = data.reduce(
      (sum, point) =>
        sum + (point[metric.key as keyof typeof point] as number),
      0,
    );
    const first =
      data[0][metric.key as keyof typeof data[0]] as number;
    const last =
      data[data.length - 1][metric.key as keyof typeof data[0]] as number;
    const change = last - first;
    const changePct = first > 0 ? (change / first) * 100 : 0;
    return { total, change, changePct };
  }, [ga.trend.data, metric.key]);

  if (!ga.integration.isConfigured) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Traffic Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          {ga.integration.message ||
            'Connect Google Analytics to view traffic trends.'}
        </CardContent>
      </Card>
    );
  }

  if (trendError && trendError.status && trendError.status !== 400) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Traffic Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-red-600">
          {trendError.data?.message ||
            trendError.message ||
            'Failed to load Google Analytics trend data.'}
        </CardContent>
      </Card>
    );
  }

  if (ga.trend.isLoading) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Traffic Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center">
          <div className="h-48 w-full animate-pulse rounded-xl bg-muted/50" />
        </CardContent>
      </Card>
    );
  }

  if (isCustomRangePending) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Traffic Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          Select a start and end date to view Google Analytics insights.
        </CardContent>
      </Card>
    );
  }

  if (!ga.trend.data.length) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Traffic Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          No page view activity detected for this period.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none shadow-sm">
      <CardHeader className="pb-2 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg font-semibold">
            Traffic Trend
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {(Object.keys(metricConfig) as Array<MetricKey>).map(metricKey => (
              <Button
                key={metricKey}
                variant={metricKey === selectedMetric ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedMetric(metricKey)}
              >
                {metricConfig[metricKey].label}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span className="text-foreground text-base font-semibold">
            {metric.label}:{' '}
            {trendSummary.total.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}
          </span>
          <span
            className={`font-medium ${
              trendSummary.change >= 0 ? 'text-emerald-600' : 'text-rose-600'
            }`}
          >
            {trendSummary.change >= 0 ? '+' : ''}
            {trendSummary.change.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}{' '}
            ({trendSummary.changePct >= 0 ? '+' : ''}
            {trendSummary.changePct.toFixed(1)}% vs first point)
          </span>
        </div>
      </CardHeader>
      <CardContent className="h-[280px]">
        <ResponsiveContainer>
          <AreaChart
            data={ga.trend.data}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient
                id={`gaTrendGradient-${selectedMetric}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={metric.color}
                  stopOpacity={0.35}
                />
                <stop
                  offset="100%"
                  stopColor={metric.color}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="4 4"
              className="stroke-muted/50"
              vertical={false}
            />
            <XAxis
              dataKey="dateLabel"
              tickLine={false}
              axisLine={true}
              tickMargin={8}
              className="text-xs text-muted-foreground"
            />
            <YAxis
              tickMargin={8}
              axisLine={true}
              tickLine={false}
              className="text-xs text-muted-foreground"
              tickFormatter={value =>
                value.toLocaleString(undefined, { maximumFractionDigits: 0 })
              }
            />
            <Tooltip
              formatter={(value: number) =>
                value.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })
              }
            />
            <Area
              type="monotone"
              dataKey={metric.key}
              stroke={metric.color}
              strokeWidth={2}
              fill={`url(#gaTrendGradient-${selectedMetric})`}
              name={metric.label}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default GaTrend;

